import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconCopy } from '@tabler/icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCase, useCaseTimeline } from '../api/cases';
import { CaseFieldsCard } from '../components/case-fields-card';
import { CaseFindingsSection } from '../components/case-findings-section';
import { CaseRequestsCard } from '../components/case-requests-card';
import { CaseConsentSection } from '../components/case-consent-section';
import { CaseStatusPill } from '../components/case-status-pill';
import { CaseTimeline } from '../components/case-timeline';
import { PatientAttributesCard } from '../components/patient-attributes-card';

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard?.writeText(text);
  } catch {
    // Best-effort.
  }
}

interface CopyIdButtonProps {
  value: string;
  label: string;
}

function CopyIdButton({ value, label }: CopyIdButtonProps) {
  return (
    <Tooltip label="Copy id">
      <ActionIcon
        variant="subtle"
        size="xs"
        aria-label={label}
        onClick={() => copyToClipboard(value)}
      >
        <IconCopy size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

interface IdRowProps {
  value: string;
  label: string;
}

/** A small monospace id line with a copy button, for the org/product/patient sections. */
function IdRow({ value, label }: IdRowProps) {
  return (
    <Group gap={4} align="center" mt={2}>
      <Text size="xs" ff="monospace" c="dimmed">
        {value}
      </Text>
      <CopyIdButton value={value} label={label} />
    </Group>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

interface CountTileProps {
  label: string;
  value: number;
  color?: string;
}

function CountTile({ label, value, color = 'gray' }: CountTileProps) {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Badge color={color} variant="light" size="lg">
        {value}
      </Badge>
    </Stack>
  );
}

export function CaseDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const caseQuery = useCase(id);
  const timelineQuery = useCaseTimeline(id);

  if (caseQuery.isPending) return <Text p="md">Loading…</Text>;
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <Stack gap="lg">
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load case.
        </Alert>
        <Anchor component={Link} to="/cases">
          Back to cases
        </Anchor>
      </Stack>
    );
  }

  const c = caseQuery.data;
  const r = c.relations;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Group gap="md">
          <Title order={2}>
            Case for {c.patient_given_name} {c.patient_family_name}
          </Title>
          <CaseStatusPill caseId={c.id} status={c.status} />
        </Group>
        <Button variant="subtle" onClick={() => navigate('/cases')}>
          Back to cases
        </Button>
      </Group>

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Group gap="xs" align="center">
            <Text size="xs" c="dimmed">
              ID
            </Text>
            <Text size="sm" ff="monospace">
              {c.id}
            </Text>
            <CopyIdButton value={c.id} label="Copy case id" />
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            <div>
              <Text size="xs" c="dimmed">
                Organisation
              </Text>
              <Text fw={500}>{c.org_name}</Text>
              <Text size="xs" c="dimmed">
                {c.org_region}
              </Text>
              <IdRow value={c.org_id} label="Copy organisation id" />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Product
              </Text>
              <Text fw={500}>{c.product_display_name}</Text>
              <Text size="xs" c="dimmed">
                {c.product_code}
              </Text>
              <IdRow value={c.product_id} label="Copy product id" />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Patient
              </Text>
              <Text fw={500}>
                {c.patient_given_name} {c.patient_family_name}
              </Text>
              <Text size="xs" c="dimmed">
                DOB {c.patient_dob}
              </Text>
              <IdRow value={c.patient_id} label="Copy patient id" />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Opened
              </Text>
              <Text>{formatDate(c.opened_at)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Updated
              </Text>
              <Text>{formatDate(c.updated_at)}</Text>
            </div>
            {c.external_reference && (
              <div>
                <Text size="xs" c="dimmed">
                  External reference
                </Text>
                <Text>{c.external_reference}</Text>
              </div>
            )}
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Relations</Title>
          <SimpleGrid cols={{ base: 2, sm: 4, md: 7 }}>
            <CountTile label="Findings" value={r.findings_count} color="blue" />
            <CountTile label="Diagnoses" value={r.diagnoses_count} color="violet" />
            <CountTile label="Histology" value={r.histology_reports_count} color="grape" />
            <CountTile label="Images" value={r.images.total} color="cyan" />
            <CountTile label="Img pending" value={r.images.pending} color="yellow" />
            <CountTile label="Img processed" value={r.images.processed} color="green" />
            <CountTile
              label="Img failed"
              value={r.images.failed + r.images.quarantined}
              color="red"
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <CaseFieldsCard
        caseId={c.id}
        externalReference={c.external_reference}
        clinicalContext={c.clinical_context}
        customFields={c.custom_fields}
      />

      <PatientAttributesCard patientId={c.patient_id} />

      <CaseFindingsSection
        caseId={c.id}
        findings={c.findings}
        caseInfo={{
          id: c.id,
          patientName: `${c.patient_given_name} ${c.patient_family_name}`,
          orgName: c.org_name,
          productName: c.product_display_name,
        }}
      />

      <CaseRequestsCard caseId={c.id} />

      <CaseConsentSection caseId={c.id} orgId={c.org_id} productId={c.product_id} />

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Timeline</Title>
          {timelineQuery.isPending && <Text c="dimmed">Loading timeline…</Text>}
          {timelineQuery.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              Could not load timeline.
            </Alert>
          )}
          {timelineQuery.data && <CaseTimeline events={timelineQuery.data.items} />}
        </Stack>
      </Card>
    </Stack>
  );
}
